/**
 * Client-side re-aggregation of a filtered block subset.
 *
 * When a filter (branch / type / agent) is active, the server's pre-aggregated
 * summary, branch breakdown and billing no longer describe what the user is
 * looking at. Rather than round-trip new query params to the Worker/service,
 * we recompute from the already-fetched blocks — mirroring the server's exact
 * semantics so unfiltered totals still match to the second:
 *   - wall-clock = union of intervals (overlap counted once)
 *     — see cloudflare/src/queries/summary.ts:mergeIntervals
 *   - open blocks (ended_at IS NULL) run to `now`
 *   - billing = derived summary × effective rates
 *     — see cloudflare/src/queries/billing.ts:computeBilling
 *
 * Only invoked while a filter is active; with no filter the server values are
 * shown verbatim.
 */

import type {
  BillingLineItem,
  BillingReport,
  BranchGroupSummary,
  ProjectSummary,
  TimeBlock,
} from './api';

export const UNKNOWN_BRANCH = 'unknown';

/** Branch label for a block, read from its metadata JSON (`$.branch`). */
export function blockBranch(block: TimeBlock): string {
  if (!block.metadata) return UNKNOWN_BRANCH;
  try {
    const parsed = JSON.parse(block.metadata) as { branch?: unknown };
    const branch = parsed?.branch;
    return typeof branch === 'string' && branch.length > 0 ? branch : UNKNOWN_BRANCH;
  } catch {
    return UNKNOWN_BRANCH;
  }
}

function mergeIntervals(intervals: ReadonlyArray<[number, number]>): number {
  if (intervals.length === 0) return 0;
  const sorted = [...intervals].sort((a, b) => a[0] - b[0]);
  let totalMs = 0;
  let curStart = -1;
  let curEnd = -1;
  for (const [s, e] of sorted) {
    if (curEnd < s) {
      if (curStart >= 0) totalMs += curEnd - curStart;
      curStart = s;
      curEnd = e;
    } else if (e > curEnd) {
      curEnd = e;
    }
  }
  if (curStart >= 0) totalMs += curEnd - curStart;
  return totalMs;
}

function blockEndMs(block: TimeBlock, nowMs: number): number {
  return block.ended_at ? new Date(block.ended_at).getTime() : nowMs;
}

function blockDurationSeconds(block: TimeBlock, nowMs: number): number {
  if (block.duration_seconds !== null) return block.duration_seconds;
  return Math.round((nowMs - new Date(block.started_at).getTime()) / 1000);
}

export function deriveSummary(
  base: ProjectSummary,
  blocks: TimeBlock[],
  nowMs: number = Date.now()
): ProjectSummary {
  let humanSeconds = 0;
  let agentLaborSeconds = 0;
  const projectIntervals: Array<[number, number]> = [];
  const agentIntervals: Array<[number, number]> = [];
  const agentAgg = new Map<string, { labor_seconds: number; block_count: number }>();

  for (const block of blocks) {
    const start = new Date(block.started_at).getTime();
    const end = blockEndMs(block, nowMs);
    const dur = blockDurationSeconds(block, nowMs);
    projectIntervals.push([start, end]);
    if (block.type === 'human') {
      humanSeconds += dur;
    } else {
      agentLaborSeconds += dur;
      agentIntervals.push([start, end]);
      const name = block.agent_name ?? 'unknown';
      const cur = agentAgg.get(name) ?? { labor_seconds: 0, block_count: 0 };
      cur.labor_seconds += dur;
      cur.block_count += 1;
      agentAgg.set(name, cur);
    }
  }

  const agent_breakdown = [...agentAgg.entries()]
    .map(([agent_name, v]) => ({ agent_name, ...v }))
    .sort((a, b) => b.labor_seconds - a.labor_seconds);

  return {
    ...base,
    human_seconds: humanSeconds,
    agent_labor_seconds: agentLaborSeconds,
    agent_wall_clock_seconds: Math.round(mergeIntervals(agentIntervals) / 1000),
    project_wall_clock_seconds: Math.round(mergeIntervals(projectIntervals) / 1000),
    block_count: blocks.length,
    agent_breakdown,
  };
}

export function deriveBranches(
  blocks: TimeBlock[],
  nowMs: number = Date.now()
): BranchGroupSummary[] {
  const groups = new Map<string, BranchGroupSummary>();
  for (const block of blocks) {
    const branch = blockBranch(block);
    const dur = blockDurationSeconds(block, nowMs);
    const cur =
      groups.get(branch) ??
      { branch, total_seconds: 0, human_seconds: 0, agent_seconds: 0, block_count: 0 };
    cur.total_seconds += dur;
    cur.block_count += 1;
    if (block.type === 'human') cur.human_seconds += dur;
    else cur.agent_seconds += dur;
    groups.set(branch, cur);
  }
  return [...groups.values()].sort((a, b) => b.total_seconds - a.total_seconds);
}

function lineAmountCents(rate: number | null, hours: number): number | null {
  if (rate === null) return null;
  return Math.round(rate * hours);
}

/** Rebuild the billing report from a (filtered) summary, reusing the server's effective rates. */
export function deriveBilling(base: BillingReport, summary: ProjectSummary): BillingReport {
  const effective = base.effective_rates;
  const lineItems: BillingLineItem[] = [];

  if (effective.standard !== null) {
    const hours = summary.project_wall_clock_seconds / 3600;
    lineItems.push({
      kind: 'standard',
      hours,
      rate_cents: effective.standard,
      amount_cents: lineAmountCents(effective.standard, hours),
    });
  } else {
    const humanHours = summary.human_seconds / 3600;
    const agentHours = summary.agent_wall_clock_seconds / 3600;
    lineItems.push({
      kind: 'human',
      hours: humanHours,
      rate_cents: effective.human,
      amount_cents: lineAmountCents(effective.human, humanHours),
    });
    lineItems.push({
      kind: 'agent',
      hours: agentHours,
      rate_cents: effective.agent,
      amount_cents: lineAmountCents(effective.agent, agentHours),
    });
  }

  return {
    ...base,
    line_items: lineItems,
    total_cents: lineItems.reduce((acc, i) => acc + (i.amount_cents ?? 0), 0),
  };
}
